import { get, head } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

/*************************************************************/

/**
 * @type {class}
 * @desc MemberInfoCommunicationSettingsWellConnect defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberInfoCommunicationSettingsWellConnectService } from 'services/memberInfoCommunicationSettingsWellConnect';
 *
 * // create service instance and send/cancel request
 * const memberInfoCommunicationSettingsWellConnectService = new MemberInfoCommunicationSettingsWellConnectService();
 * const result = await memberInfoCommunicationSettingsWellConnectService.fetchData( '<user-id>' ); // fetch data
 * memberInfoCommunicationSettingsWellConnectService.cancel(); // cancel an active request
 */
export class MemberInfoCommunicationSettingsWellConnectService extends HttpService {

    // fetch component data
    async fetchComponentData( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                const componentData = response.body.component;
                const state = componentData.data.state;
                const visibility = componentData.visibility;

                return {
                    status: SERVICE_RESPONSE_STATUS.SUCCESS,
                    data: {
                        staticDescription: componentData.structure.staticDescription,
                        dynamicDescription: head( getVisibleItems( componentData.structure.dynamicDescription, state ) ),
                        phoneNumberSection: state.memberOptedIn ? componentData.structure.phoneNumberSection : {},
                        actions: getVisibleItems( componentData.structure.actions, visibility ),
                        values: state.values
                    }
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE
                };
            }
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE
            };
        }
    }

    // fetch opt in form data
    async fetchOptInForm( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                const componentData = response.body.component;
                const visibility = componentData.visibility;

                return {
                    status: SERVICE_RESPONSE_STATUS.SUCCESS,
                    data: {
                        title: componentData.structure.title,
                        infoSection: componentData.structure.infoSection,
                        optInSection: get( visibility, 'optInSection', false ) ? componentData.structure.optInSection : {},
                        actions: getVisibleItems( componentData.structure.actions, visibility )
                    }
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE
                };
            }
            
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE
            };
        }
    }

    // submit opt in form
    async submitOptInForm( { url = {}, data = {} } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data', {} )
            };
        } catch ( err ) {
            return SERVICE_RESPONSE_STATUS.FAILURE;
        }
    }

    // fetch edit well connect phone form data
    async fetchEditWellConnectPhoneForm( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                const componentData = response.body.component;
                const visibility = componentData.visibility;

                return {
                    status: SERVICE_RESPONSE_STATUS.SUCCESS,
                    data: {
                        values: componentData.data.state.values,
                        title: componentData.structure.title,
                        fields: getVisibleItems( componentData.structure.fields, visibility ),
                        actions: getVisibleItems( componentData.structure.actions, visibility )
                    }
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE
                };
            }
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE
            };
        }
    }

    // submit edit well connect phone form
    async submitEditWellConnectPhone( { url = {}, data = {} } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data', {} )
            };
        } catch ( err ) {
            return SERVICE_RESPONSE_STATUS.FAILURE;
        }
    }

    // submit opt out request
    async submitOptOutRequest( {url = {}, data = {}} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data', {} )
            };
        } catch ( err ) {
            return SERVICE_RESPONSE_STATUS.FAILURE;
        }
    }

    // submit not interested request
    async submitNotInterestedRequest( {url = {}, data = {}} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data', {} )
            };
        } catch ( err ) {
            return SERVICE_RESPONSE_STATUS.FAILURE;
        }
    }
}
